import { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import PrintIcon from '@mui/icons-material/Print'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { PickOrderDetailFragment } from '../../../../graphql'
import TableHeaderCell from '../../components/TableHeaderCell'
import { demandRequestPath, fulfillmentOrderPath, transferOrderPath } from '../chain'

type Fulfillment = PickOrderDetailFragment['fulfillments'][number]

/** `FO-165 (REQ-1234) · 12 units`, per order in the carton. */
function CartonContents( { fulfillment }: { fulfillment: Fulfillment } ) {
	const byOrder = new Map<string, { orderNumber: number | string, request: { id: string, requestNumber: number | string } | null, units: number }>()
	for ( const line of fulfillment.lines ) {
		const item = line.fulfillmentOrderLineItem
		const entry = byOrder.get( item.fulfillmentOrder.id ) ?? {
			orderNumber: item.fulfillmentOrder.orderNumber,
			request: item.demandRequestLineItem?.demandRequest ?? null,
			units: 0,
		}
		entry.units += line.quantityFulfilled
		byOrder.set( item.fulfillmentOrder.id, entry )
	}

	return (
		<Stack spacing={0.25}>
			{[ ...byOrder.values() ].filter( ( entry ) => entry.units > 0 ).map( ( entry ) => (
				<Typography key={entry.orderNumber} variant="body2" component="div">
					<Link to={fulfillmentOrderPath( entry.orderNumber )}>FO-{entry.orderNumber}</Link>
					{entry.request && (
						<>
							{' '}(<Link to={demandRequestPath( entry.request.id )}>REQ-{entry.request.requestNumber}</Link>)
						</>
					)}
					{' · '}{entry.units} {entry.units === 1 ? 'unit' : 'units'}
				</Typography>
			) )}
		</Stack>
	)
}

/**
 * What a finished run shipped.
 *
 * A completed pick order has no draft cartons left to show — inventory-api turned
 * each one into a Fulfillment, and that is what the run carries afterwards. One
 * row per Fulfillment, in carton order, with the transfer order it became and
 * the orders and requests it carried.
 */
export default function CompletedRunSummary( {
	pickOrder,
	onPrintPackingSlips,
}: {
	pickOrder: PickOrderDetailFragment,
	onPrintPackingSlips: () => void,
} ): ReactElement {
	const fulfillments = [ ...pickOrder.fulfillments ]
		.sort( ( a, b ) => ( a.cartonNumber ?? 0 ) - ( b.cartonNumber ?? 0 ) )

	return (
		<Stack spacing={2}>
			<Alert
				severity="success"
				action={fulfillments.length > 0 ? (
					<Button color="inherit" size="small" startIcon={<PrintIcon />} onClick={onPrintPackingSlips}>
						Print packing slips
					</Button>
				) : undefined}
			>
				This pick order is complete. {fulfillments.length} {fulfillments.length === 1 ? 'carton' : 'cartons'} packed.
			</Alert>

			{fulfillments.length > 0 && (
				<Box>
					<TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'pickFlow.cardBorder' }}>
						<Table>
							<TableHead>
								<TableRow sx={{ bgcolor: 'grey.50' }}>
									<TableHeaderCell>Carton</TableHeaderCell>
									<TableHeaderCell>Tracking number</TableHeaderCell>
									<TableHeaderCell>Transfer order</TableHeaderCell>
									<TableHeaderCell>Contents</TableHeaderCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{fulfillments.map( ( fulfillment ) => (
									<TableRow key={fulfillment.id}>
										<TableCell>Carton {fulfillment.cartonNumber ?? '—'}</TableCell>
										<TableCell>
											{fulfillment.trackingNumber ?? (
												<Typography component="span" variant="body2" color="text.secondary">
													None
												</Typography>
											)}
										</TableCell>
										<TableCell>
											{fulfillment.systemTransferOrder ? (
												<Link to={transferOrderPath( fulfillment.systemTransferOrder.id )}>
													TO-{fulfillment.systemTransferOrder.id}
												</Link>
											) : '—'}
										</TableCell>
										<TableCell><CartonContents fulfillment={fulfillment} /></TableCell>
									</TableRow>
								) )}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			)}
		</Stack>
	)
}
